import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentStep,
  getOnBoardingID,
  getOnBoardingName,
  getOnBoardingReviewsSelector,
  getStepNumber,
  isLoading,
} from '../../../../redux/onBoarding/selector';
import useOnBoardingCurrentStep from '../../../../hooks/useOnBoardingCurrentStep';
import { OnBoardingTypes } from '../../../../redux/onBoarding/types';
import { OnBoardingReviewsPage, OnBoardingReviewsWrapper } from './OnBoardingReviews.style';
import { Container, CustomButton, FlagTypes, ReviewCard } from '@mindly/ui-components';
import OnBoardingFooter from '../../components/OnBoardingFooter/OnBoardingFooter';
import useOnBoardingNextStep from '../../../../hooks/useOnBoardingNextStep';
import OnBoardingHeader from '../../components/OnBoardingHeader/OnBoardingHeader';
import { FormattedMessage } from 'react-intl';
import { ImpactStyle } from '@capacitor/haptics';
import { fetchOnBoardingReviews } from '../../../../redux/onBoarding/thunk';
import useLangs from '../../../../context/LangsContext';
import { FunnelTypeEnum, useAnalytics } from 'src/shared';
import { setSplashColor } from 'src/shared/utils/splashColor';
import { Style } from '@capacitor/status-bar';
import { globalVariables } from 'src/redux/firestoreGlobalVariables/selector';
import { useTranslation } from 'react-i18next';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

interface OnBoardingReviewsProps extends RouteComponentProps<{ boardId: string; id: string }> {}

const OnBoardingReviews: FC<OnBoardingReviewsProps> = ({ match }) => {
  const { id } = match.params;

  const dispatch = useDispatch();
  const { locale } = useLangs();

  const pageCounterRef = useRef(0);
  const { i18n } = useTranslation();

  const currentStep = useSelector(getCurrentStep);
  const onBoardingName = useSelector(getOnBoardingName);
  const stepNumber = useSelector(getStepNumber);
  const isLoadingSelector = useSelector(isLoading);
  const reviews = useSelector(getOnBoardingReviewsSelector);
  const onBoardingId = useSelector(getOnBoardingID);
  const globalVars = useSelector(globalVariables);

  const [isPageView, setIsPageView] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);

  const currentStepAction = useOnBoardingCurrentStep({
    ...match.params,
    type: OnBoardingTypes.REVIEWS,
  });
  const nextStepAction = useOnBoardingNextStep();
  const { trackPredefinedEvent } = useAnalytics();

  useIonViewDidEnter(() => {
    currentStepAction();

    setIsPageView(true);
    setIsEventTracked(false);

    setSplashColor('#177590', Style.Dark);
  }, [currentStepAction, id, currentStep]);

  useIonViewDidLeave(() => {
    setIsPageView(false);
    setIsEventTracked(false);
    pageCounterRef.current = 0;
  });

  useEffect(() => {
    if (!isPageView) return;
    if (pageCounterRef.current > 0) return;
    pageCounterRef.current += 1;
  }, [isPageView, stepNumber, onBoardingName, pageCounterRef]);

  const handleAnalyticEvent = () => {
    if (!onBoardingId) return;

    trackPredefinedEvent('FUNNEL_SCREEN_VIEWED', {
      funnel_id: onBoardingId,
      funnel_name: onBoardingName,
      funnel_type:
        onBoardingId === globalVars?.defaultOnBoardingId?.[i18n.language]
          ? FunnelTypeEnum.DEFAULT
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: id,
      screen_name: `Reviews screen: ${id}`,
      screen_order: stepNumber,
    });
    setIsEventTracked(true);
  };

  useEffect(() => {
    if (!isPageView) return;
    if (isLoadingSelector) return;
    if (!onBoardingId || !onBoardingName || !stepNumber) return;
    if (isEventTracked) return;

    handleAnalyticEvent();
  }, [isPageView, isLoadingSelector, onBoardingId, onBoardingName, stepNumber, isEventTracked]);

  const handleNextStep = useCallback(async () => {
    await nextStepAction();
  }, [nextStepAction]);

  return (
    <OnBoardingReviewsPage>
      <OnBoardingHeader hideBackButton />
      <IonContent className={'blue'}>
        <Container>
          <h2 className={'reviews-title'}>
            <FormattedMessage
              id={'on-boarding.reviews.title'}
              defaultMessage={'Відгуки клієнтів які знайшли свого психолога'}
            />
          </h2>
          <OnBoardingReviewsWrapper>
            {reviews.data.map((r) => (
              <ReviewCard
                key={r.id}
                className={'review-item'}
                reviewerAvatar={r.client_avatar ?? ''}
                reviewerFirstName={r.client_first_name ?? ''}
                reviewerLastName={r.client_last_name ?? ''}
                reviewerCountry={r.client_country as keyof typeof FlagTypes}
                translations={{
                  showMore: '',
                  showAllReviews: '',
                }}
                reviewText={r.text_of_review ?? ''}
                starsCount={r.review_rating}
                type={'list'}
                reviewedAtInSeconds={r.created_at}
                cutReview={false}
              />
            ))}
          </OnBoardingReviewsWrapper>
          <IonInfiniteScroll
            onIonInfinite={async (ev) => {
              await dispatch(fetchOnBoardingReviews(locale));
              ev.target.complete();
            }}
            disabled={reviews.isDisabled}
          >
            <IonInfiniteScrollContent />
          </IonInfiniteScroll>
        </Container>
      </IonContent>
      <OnBoardingFooter>
        <CustomButton
          type="button"
          buttonType="primary"
          onClick={async () => {
            isomorficHapticsImpact({ style: ImpactStyle.Light });
            await handleNextStep();
          }}
          disabled={isLoadingSelector}
          loading={isLoadingSelector}
        >
          <FormattedMessage id={'general.btn.next'} defaultMessage={'Далі'} />
        </CustomButton>
      </OnBoardingFooter>
    </OnBoardingReviewsPage>
  );
};

export default OnBoardingReviews;
